import React, {FormEvent, useEffect, useMemo, useRef, useState} from 'react';

import {addYears, isAfter, subDays} from 'date-fns';
import {useGate, useStore} from 'effector-react';
import {saveAs} from 'file-saver';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath, useHistory} from 'react-router-dom';
import {toast} from 'react-toastify';

import {exportRecallAuditLog} from 'api/recallAudit';
import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {ReactComponent as ExportIcon} from 'assets/svg/download.svg';
import {Page} from 'components/common/Page';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {DateRange} from 'components/ui-kit/DateRange';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';
import {Popover} from 'components/ui-kit/Popover';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {
    ButtonsContainer,
    CloseButton,
    HeaderWrapper,
} from 'components/ui-kit/Table2.0/HeaderWrapper/Filtration/styled';
import {ROUTE} from 'core/routes';
import {profile$} from 'models/auth';
import {
    filtersChanged,
    filtersReset,
    Gate,
    pageNumberChanged,
    pageSizeChanged,
    sortingsChanged,
    tableState$,
} from 'models/recallAudit';
import {mapRecallAuditFilters} from 'models/recallAudit/mappers';
import {GridFieldConfig, InnerFilters, UserRoles} from 'types';
import {
    RecallAuditAccessors,
    RecallAuditEntry,
    RecallAuditEventType,
    RecallAuditExportFormat,
} from 'types/recall-audit';
import {handleNil} from 'utils/handleNil';

import {
    BreakAllValue,
    EventId,
    ExportFields,
    ExportFilterContent,
    ExportPopoverAnchor,
    PageContent,
    Toolbar,
} from './styled';
import {formatLocalDateTime, translateUserRole} from './utils';

interface MappedAuditRow extends Record<string, React.ReactNode> {
    entry: RecallAuditEntry;
}

type ExportPeriod = [Date | null, Date | null];

interface ExportControlProps {
    filters: InnerFilters;
    pageNumber: number;
    pageSize: number;
}

interface ExportFormProps {
    initialPeriod: ExportPeriod;
    pending: boolean;
    onCancel: () => void;
    onExport: (format: RecallAuditExportFormat, period: ExportPeriod) => void;
}

const ExportForm = ({initialPeriod, pending, onCancel, onExport}: ExportFormProps) => {
    const {t} = useTranslation();
    const [format, setFormat] = useState<RecallAuditExportFormat | null>(null);
    const [period, setPeriod] = useState<ExportPeriod>(initialPeriod);
    const [periodRevision, setPeriodRevision] = useState(0);
    const formatOptions = useMemo(
        () =>
            Object.values(RecallAuditExportFormat).map((value) => ({
                value,
                label: value,
            })),
        [],
    );

    const submit = (event: FormEvent) => {
        event.preventDefault();
        if (format && period) onExport(format, period);
    };
    const changePeriod = (value: ExportPeriod | undefined) => {
        setPeriod(value ?? [null, null]);
        setPeriodRevision((revision) => revision + 1);
    };
    return (
        <form onSubmit={submit}>
            <ExportFields>
                <Select
                    label={t('pages.recallAudit.export.format')}
                    options={formatOptions}
                    value={formatOptions.find((option) => option.value === format)}
                    onChange={(option: SelectOption<RecallAuditExportFormat>) =>
                        setFormat(option?.value ?? null)
                    }
                />
                <DateRange
                    key={periodRevision}
                    label={t('pages.recallAudit.export.period')}
                    value={period}
                    onChange={changePeriod}
                    keepHeaderSelectOpenOnBlur
                />
            </ExportFields>
            <ButtonsContainer>
                <Button
                    size="sm"
                    variant={ButtonVariant.Outline}
                    disabled={pending}
                    type="button"
                    onClick={onCancel}
                >
                    {t('components.form.Cancel')}
                </Button>
                <Button
                    size="sm"
                    variant={ButtonVariant.Normal}
                    isLoading={pending}
                    disabled={!format || !period?.[0] || !period?.[1]}
                    type="submit"
                >
                    {t('pages.recallAudit.export.action')}
                </Button>
            </ButtonsContainer>
        </form>
    );
};

const ExportControl = React.memo(({filters, pageNumber, pageSize}: ExportControlProps) => {
    const {t} = useTranslation();
    const anchorRef = useRef<HTMLDivElement>(null);
    const [, setAnchorReady] = useState(false);
    const [opened, setOpened] = useState(false);
    const [pending, setPending] = useState(false);
    const initialPeriod = useMemo<ExportPeriod>(() => {
        const filterPeriod = filters.eventTimestamp as ExportPeriod | undefined;

        if (filterPeriod?.[0] && filterPeriod?.[1]) return filterPeriod;

        const today = new Date();
        return [subDays(today, 7), today];
    }, [filters.eventTimestamp]);

    useEffect(() => {
        setAnchorReady(true);
    }, []);

    const close = () => {
        if (!pending) setOpened(false);
    };

    const exportLog = async (format: RecallAuditExportFormat, period: ExportPeriod) => {
        const [from, to] = period;
        if (!from || !to) return;
        if (isAfter(to, addYears(from, 1))) {
            toast.error(t('pages.recallAudit.export.periodTooLong'));
            return;
        }

        try {
            setPending(true);
            const exportFilters = mapRecallAuditFilters({
                ...filters,
                eventTimestamp: period,
            });

            if (!exportFilters.from || !exportFilters.to) return;

            const result = await exportRecallAuditLog({
                ...exportFilters,
                from: exportFilters.from,
                to: exportFilters.to,
                page: pageNumber,
                size: pageSize,
                format,
            });
            const extension = {
                [RecallAuditExportFormat.CSV]: 'csv',
                [RecallAuditExportFormat.EXCEL]: 'xlsx',
            }[format];
            saveAs(result.data, `recall-audit.${extension}`);
            if (result.truncated) {
                toast.warn(
                    t('pages.recallAudit.export.truncated', {
                        limit: result.limit ?? 10000,
                    }),
                );
            }
            setOpened(false);
        } finally {
            setPending(false);
        }
    };

    return (
        <ExportPopoverAnchor ref={anchorRef}>
            <Button
                size="sm"
                variant={ButtonVariant.Normal}
                onClick={() => setOpened(true)}
                icon={<Icon source={ExportIcon} width={16} height={16} />}
            >
                {t('pages.recallAudit.export.action')}
            </Button>
            <Popover
                isOpen={opened}
                onClose={close}
                pivotElement={anchorRef.current ?? undefined}
                offset={{
                    x: 0,
                    y: -((anchorRef.current?.getBoundingClientRect().height ?? 0) + 12),
                }}
            >
                <ExportFilterContent>
                    <CloseButton
                        disabled={pending}
                        icon={
                            <Icon source={CrossIcon} fillColor="#C4C4C4" width={12} height={12} />
                        }
                        onClick={close}
                    />
                    <HeaderWrapper>{t('pages.recallAudit.export.title')}</HeaderWrapper>
                    <ExportForm
                        initialPeriod={initialPeriod}
                        pending={pending}
                        onCancel={close}
                        onExport={exportLog}
                    />
                </ExportFilterContent>
            </Popover>
        </ExportPopoverAnchor>
    );
});

ExportControl.displayName = 'ExportControl';

const getColumns = (
    t: TFunction,
    eventTypeOptions: SelectOption<RecallAuditEventType>[],
): GridFieldConfig[] => [
    {
        id: RecallAuditAccessors.eventId,
        header: t('pages.recallAudit.columns.eventId'),
        width: 230,
    },
    {
        id: RecallAuditAccessors.batchId,
        header: t('pages.recallAudit.columns.batchId'),
        width: 120,
    },
    {
        id: RecallAuditAccessors.eventType,
        header: t('pages.recallAudit.columns.eventType'),
        width: 210,
        filter: <Select isClearable options={eventTypeOptions} />,
    },
    {
        id: RecallAuditAccessors.organizationName,
        header: t('pages.recallAudit.columns.organization'),
        width: 180,
        filter: <Input />,
    },
    {
        id: RecallAuditAccessors.organizationTin,
        header: t('pages.recallAudit.columns.tin'),
        width: 130,
        filter: <Input />,
    },
    {
        id: RecallAuditAccessors.eventTimestamp,
        header: t('pages.recallAudit.columns.dateTime'),
        width: 170,
        filter: <DateRange />,
    },
    {
        id: RecallAuditAccessors.userFullName,
        header: t('pages.recallAudit.columns.userName'),
        width: 180,
        filter: <Input />,
    },
    {
        id: RecallAuditAccessors.userRole,
        header: t('pages.recallAudit.columns.userRole'),
        width: 160,
    },
];

export const RecallAuditLogPage = () => {
    const {t} = useTranslation();
    const history = useHistory();
    useGate(Gate);

    const {data, pageNumber, pageSize, filters, sortings, isLoading} = useStore(tableState$);
    const profile = useStore(profile$);
    const canExport = profile?.roles.some((role) =>
        [UserRoles.ROLE_MPL_OPERATOR_ADMIN, UserRoles.ROLE_MPL_TS_DIRECTOR].includes(role),
    );

    const eventTypeOptions = useMemo(
        () =>
            Object.values(RecallAuditEventType).map((value, index) => ({
                id: String(index),
                value,
                label: t(`pages.recallAudit.eventTypes.${value}`),
            })),
        [t],
    );
    const columns = useMemo(
        () =>
            mapTableColumns(
                getColumns(t, eventTypeOptions),
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [eventTypeOptions, filters, sortings, t],
    );
    const mappedData = useMemo<MappedAuditRow[]>(
        () =>
            (data?.data ?? []).map((entry) => ({
                entry,
                [RecallAuditAccessors.eventId]: (
                    <EventId>
                        <Link
                            to={generatePath(ROUTE.RECALL_AUDIT_DETAIL, {
                                eventId: entry.eventId,
                            })}
                            onClick={(event) => event.stopPropagation()}
                        >
                            {entry.eventId}
                        </Link>
                    </EventId>
                ),
                [RecallAuditAccessors.batchId]: handleNil(entry.batchId),
                [RecallAuditAccessors.eventType]: t(
                    `pages.recallAudit.eventTypes.${entry.eventType}`,
                ),
                [RecallAuditAccessors.organizationName]: handleNil(
                    entry.organizationName ?? entry.organizationId,
                ),
                [RecallAuditAccessors.organizationTin]: handleNil(entry.organizationTin),
                [RecallAuditAccessors.eventTimestamp]: formatLocalDateTime(entry.eventTimestamp),
                [RecallAuditAccessors.userFullName]: handleNil(entry.userFullName ?? entry.userId),
                [RecallAuditAccessors.userRole]: (
                    <BreakAllValue>{translateUserRole(t, entry.userRole)}</BreakAllValue>
                ),
            })),
        [data, t],
    );

    const openEntry = (entry: RecallAuditEntry) => {
        history.push(generatePath(ROUTE.RECALL_AUDIT_DETAIL, {eventId: entry.eventId}));
    };

    const toolbar = (
        <Toolbar>
            {canExport && (
                <ExportControl filters={filters} pageNumber={pageNumber} pageSize={pageSize} />
            )}
        </Toolbar>
    );

    return (
        <Page title={t('menu.activityLog')}>
            <PageContent>
                <Table
                    columns={columns}
                    data={mappedData}
                    pageIndex={pageNumber}
                    pageCount={data?.totalPages}
                    totalRowsCount={data?.totalElements}
                    onPageChange={pageNumberChanged}
                    onPageSizeChange={pageSizeChanged}
                    isLoading={isLoading}
                    hideSetting
                    headerControls={toolbar}
                    onRowClick={(row) => openEntry(row.original.entry)}
                />
            </PageContent>
        </Page>
    );
};

export {RecallAuditDetailPage} from './Detail';
